import React from "react";
import {
  Dialog, DialogContent, Box, Typography, IconButton, Divider, FormControl, InputLabel, Select, MenuItem,
  Stack, Button, Avatar, Paper, Chip
} from "@mui/material";
import { Close, Edit } from "@mui/icons-material";
import Config from "components/ClientConfig";

/**
 * ComboPickerSheet
 * - Shows combo image header
 * - For each combo category:
 *    * When no selection yet OR currently editing => show <Select>
 *    * After selection => compact summary row with "Edit" + price label based on slot priceType/value
 * - Confirm enabled when all categories have a selected product
 *
 * Props:
 *  - open
 *  - combo { id, name, imageSmallUrl, categories: [{id,name,categoryId,subcategoryId,priceType,priceValueC}], fixedItems? }
 *  - productsPool: normalized products list [{id, name, categoryId, subcategoryId, vatId, sellable, imageSmallUrl, ...}]
 *  - priceList { priceByProductId }
 *  - effectivePrices: Map|Object of effective product prices (RON) by productId
 *  - promoByProductId: Object map of productId -> promo price (RON)
 *  - promoByComboId:   Object map of comboId   -> promo base price (RON)  👈 NEW
 *  - onClose()
 *  - onConfirm(selectionsMap, details[], meta?: { isComboPromo: boolean })
 */
export default function ComboPickerSheet({
  open,
  combo,
  productsPool,
  priceList,
  effectivePrices,
  promoByProductId,
  promoByComboId, // NEW
  onClose,
  onConfirm,
}) {
  const [selections, setSelections] = React.useState({});
  const [editingCatId, setEditingCatId] = React.useState(null);

  const byId = React.useMemo(() => {
    const m = new Map();
    (productsPool || []).forEach(p => m.set(p.id, p));
    return m;
  }, [productsPool]);

  const displayName = (productId, fallback) =>
    byId.get(productId)?.name || fallback || "Produs";

  React.useEffect(() => {
    if (open) {
      setSelections({});
      setEditingCatId(null);
    }
  }, [open, combo?.id]);

  if (!open || !combo) return null;

  const money = (n, c = "RON") =>
    typeof n === "number" ? new Intl.NumberFormat("ro-RO",{style:"currency",currency:c}).format(n) : "-";

  /**
   * Price label for an item using slot priceType/value and pricelist:
   *  - type 0: "0 RON (inclus)"
   *  - type 1: price from promoMap > pricelist for productId
   *  - type 2: set value from combo (priceValueC)
   *
   * item: { priceType, productId? (or id), priceValueC? }
   */
  function labelForItem(item, priceList, promoMap) {
    const productId = item.productId ?? item.id;
    if (item.priceType === 0) return "0 RON (inclus)";
    if (item.priceType === 1) {
      const promo = promoMap?.[productId];
      const pl    = priceList?.priceByProductId?.[productId];
      const v = (promo != null) ? promo : pl;
      return money(typeof v === "number" ? v : 0);
    }
    return money((item.priceValueC || 0) / 100);
  }

  // Accept Map OR plain object for effectivePrices
  const hasEffPrice = (prod) => {
    if (!effectivePrices) return false;
    if (typeof effectivePrices.get === "function") {
      return effectivePrices.get(prod.id) != null;
    }
    return effectivePrices[prod.id] != null;
  };

  // Build options per category with filters
  const optionsByCat = (combo.categories || []).map(cat => {
    const opts = (productsPool || [])
      .filter(p => p.status === 2 && p.isForSale === true)
      .filter(p => {
        const okSub = !cat.subcategoryId || String(p.subcategoryId) === String(cat.subcategoryId);
        const okCat = cat.subcategoryId ? true : (!cat.categoryId || String(p.categoryId) === String(cat.categoryId));
        return okCat && okSub;
      })
      .filter(p => {
        // A product can be chosen only if it has a price in promo or pricelist
        return hasEffPrice(p) || (priceList?.priceByProductId?.[p.id] != null) || (promoByProductId?.[p.id] != null);
      });
    return { cat, opts };
  });

  optionsByCat.forEach(({ cat }) => {
    if (!cat.subcategoryId || !cat.categoryId) return;

    const any = (productsPool || []).find(
      p =>
        String(p.subcategoryId) === String(cat.subcategoryId) &&
        String(p.categoryId) !== String(cat.categoryId)
    );

    if (any) {
      console.log("⚠️ Subcategory not linked to category", {
        comboId: combo.id,
        comboName: combo.name,
        slotId: cat.id,
        slotName: cat.name,
        expectedCategoryId: cat.categoryId,
        actualCategoryId: any.categoryId,
        subcategoryId: cat.subcategoryId,
        productId: any.id,
        productName: any.name,
      });
    }
  });

  const currentSel = (catId) => selections[catId] || null;

  const handleChange = (catId, productId) => {
    const opts = optionsByCat.find(x => x.cat.id === catId)?.opts || [];
    const prod = opts.find(p => p.id === productId);
    if (prod) {
      setSelections(prev => ({ ...prev, [catId]: prod }));
      // collapse the select for this category after choosing
      setEditingCatId(null);
    }
  };

  const allSelected = (combo.categories || []).every(cat => !!currentSel(cat.id));

  const confirm = () => {
    const details = (combo.categories || []).map(cat => {
      const sel = currentSel(cat.id);
      const prod = sel ? byId.get(sel.id) : null;
      return {
        categoryId: cat.id,
        productId: sel?.id || null,
        priceType: cat.priceType,
        priceValueC: cat.priceValueC,
        vatId: sel?.vat || sel?.vatId || prod?.vatId || null,
        // 👇 ensure we pass the real product name
        name: prod?.name || sel?.name || "",
      };
    });

    // Determine if this combo is promo (base). This tells skipLine to route to orderComboPromotionEntry_order.
    const isComboPromo = promoByComboId?.[combo.id] != null;

    onConfirm && onConfirm(selections, details, { isComboPromo });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: 3,
          width: { xs: "95vw", sm: "70vw" },
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header with image */}
        <Box sx={{ position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
              Options for: {combo.name}
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
          <Divider />
          {(combo.imageSmallUrl ||
            (combo.comboImage_productCombo &&
              combo.comboImage_productCombo[0]?.image)) && (
            <Box sx={{ px: 2, pt: 1.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: 2,
                }}
              >
                <Box
                  component="img"
                  src={
                    combo.imageSmallUrl
                      ? combo.imageSmallUrl
                      : `${Config.BASE_URL}${combo.comboImage_productCombo[0].image}`
                  }
                  alt={combo.name}
                  sx={{ maxHeight: 160, maxWidth: "100%", objectFit: "contain" }}
                />
              </Paper>
            </Box>
          )}
        </Box>

        {/* Body */}
        {combo.fixedItems?.length ? (
          <Box sx={{ px: 2, pt: combo.imageSmallUrl ? 1 : 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Included in combo
            </Typography>
            {combo.fixedItems.map((fi) => (
              <Box
                key={fi.id}
                sx={{
                  px: 1.25,
                  py: 0.75,
                  mb: 0.75,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                  {displayName(fi.productId, fi.name)}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {labelForItem(fi, priceList, promoByProductId)}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}

        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            height: "100%",
          }}
        >
          {(combo.categories || []).map((cat) => {
            const sel = currentSel(cat.id);
            const opts =
              optionsByCat.find((x) => x.cat.id === cat.id)?.opts || [];
            const isEditing = !sel || editingCatId === cat.id;

            return (
              <Box key={cat.id}>
                {isEditing ? (
                  <FormControl fullWidth size="medium">
                    <InputLabel id={`label-${cat.id}`}>{cat.name}</InputLabel>
                    <Select
                      labelId={`label-${cat.id}`}
                      label={cat.name}
                      value={sel?.id || ""}
                      onChange={(e) => handleChange(cat.id, e.target.value)}
                      MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                    >
                      {opts.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                              width: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              <Avatar
                                variant="rounded"
                                src={p.imageSmallUrl || undefined}
                                sx={{ width: 28, height: 28 }}
                              >
                                {!p.imageSmallUrl && (p.name?.[0] || "?")}
                              </Avatar>
                              <Typography variant="body2" noWrap>
                                {p.name}
                              </Typography>
                            </Stack>

                            <Typography variant="caption" color="text.secondary">
                              {labelForItem(
                                {
                                  priceType: cat.priceType,
                                  productId: p.id,
                                  priceValueC: cat.priceValueC || 0,
                                },
                                priceList,
                                promoByProductId
                              )}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      px: 1.25,
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderRadius: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ minWidth: 0 }}
                    >
                      <Typography variant="subtitle2" noWrap>
                        {cat.name}
                      </Typography>
                      <Chip size="small" label={sel?.name} />
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {labelForItem(
                          {
                            priceType: cat.priceType,
                            productId: sel?.id,
                            priceValueC: cat.priceValueC || 0,
                          },
                          priceList,
                          promoByProductId
                        )}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setEditingCatId(cat.id)}
                        aria-label="Edit"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                )}
              </Box>
            );
          })}

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={1}>
            <Button onClick={onClose}>Back</Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              onClick={confirm}
              disabled={!allSelected}
            >
              Add to cart
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}